import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  createReservation,
  resetSuccess,
  resetError,
} from "../../features/reservationSlice";
import { updateUser } from "../../features/userSlice";
import "./Reservation.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Russian } from "flatpickr/dist/l10n/ru.js";
import { translations, normalizeLang } from "../../i18n";

const roomNames = {
  vinil: "Винил-рум",
  radiola: "Радиола-рум",
  livingrm: "Малая гостиная целиком",
  disco: "Зал discopolaroid целиком",
  bigliving: "Большая гостиная целиком",
  residence: "Резиденция целиком",
};

const formatPhoneDisplay = (value) => {
  const digits = value.replace(/\D/g, "").replace(/^7/, "");
  const clean = digits.slice(0, 10);
  let formatted = "+7";
  if (clean.length > 0) formatted += ` (${clean.slice(0, 3)}`;
  if (clean.length >= 4) formatted += `) ${clean.slice(3, 6)}`;
  if (clean.length >= 7) formatted += `-${clean.slice(6, 8)}`;
  if (clean.length >= 9) formatted += `-${clean.slice(8, 10)}`;
  return formatted;
};

const checkDate = (val, t) => {
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return t.dateFormatError;
  const [d, m, y] = val.split(".").map(Number);
  const dt = new Date(y, m - 1, d);
  if (dt.getDate() !== d || dt.getMonth() !== m - 1 || dt.getFullYear() !== y) {
    return t.dateInvalidError;
  }
  if (d === 1 && m === 1) return t.dateClosedError;
  return "";
};

const formatDate = (dateObj) => {
  if (!dateObj) return "";
  const d = String(dateObj.getDate()).padStart(2, "0");
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const y = dateObj.getFullYear();
  return `${d}.${m}.${y}`;
};

const checkGuests = (val, t) =>
  !/^\d+$/.test(val) || +val < 1 || +val > 999 ? t.guestsRangeError : "";

const checkTime = (val, t) => {
  if (!/^\d{2}:\d{2}$/.test(val)) return t.timeFormatError;
  const [h, m] = val.split(":").map(Number);
  return h > 23 || m > 59 ? t.timeInvalidError : "";
};

const checkDuration = (val, isSecretRoom, t) => {
  const secretError = t.durationSecretError;
  const defaultError = t.durationDefaultError;

  if (!val) return isSecretRoom ? secretError : defaultError;

  const numVal = parseFloat(val);
  const max = isSecretRoom ? 10 : 24;

  if (isNaN(numVal) || numVal < 1 || numVal > max) {
    return isSecretRoom ? secretError : defaultError;
  }

  if (isSecretRoom) {
    if (Math.round(numVal * 2) !== numVal * 2) {
      return t.durationStepError;
    }
    return "";
  }

  const validValues = ["1", "1.5", "2", "2.5", "3", "3.5", "4"];
  if (!validValues.includes(val)) return defaultError;

  return "";
};

const formatSecretDurationLabel = (val, t) => {
  const numVal = parseFloat(val);
  if (isNaN(numVal)) return val;

  if (numVal === 1) return t.duration_1h;

  const isHalf = numVal % 1 !== 0;

  if (isHalf || (numVal >= 2 && numVal <= 4)) {
    return `${val} ${t.reservationHour}`;
  }

  return `${val} ${t.reservationHour2}`;
};

const getBookingWeekday = (dateStr) => {
  const [d, m, y] = dateStr.split(".").map(Number);
  const bookingDate = new Date(y, m - 1, d);
  return bookingDate.getDay();
};

const isAllowedBookingTime = (dateStr, timeStr) => {
  const [h, min] = timeStr.split(":").map(Number);
  const totalMinutes = h * 60 + min;
  const day = getBookingWeekday(dateStr);

  const isEarlyEnough =
    (day >= 0 && day <= 4 && totalMinutes <= 120) ||
    (day >= 5 && totalMinutes <= 240);

  const isLateEnough = totalMinutes >= 12 * 60;

  return isEarlyEnough || isLateEnough;
};

const stripInvalidChars = (name) =>
  name.replace(/[^\p{L}\s'-]/gu, "").trim();

const hasInvalidNameInput = (name) => /[^\p{L}\s'-]/u.test(name);

const Reservation = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.data);
  const { loading, success, error } = useSelector((s) => s.reservation);

  const lang = normalizeLang(user?.languageCode);
  const t = translations[lang];

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const room = query.get("room");

  const [bookingName, setBookingName] = useState("");
  const [nameFormatError, setNameFormatError] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState("");
  const [time, setTime] = useState("");
  const [defaultTime, setDefaultTime] = useState("");
  const [timeTouched, setTimeTouched] = useState(false);
  const [duration, setDuration] = useState("");
  const [description, setDescription] = useState("");
  const roomName = roomNames[room] || "";
  const isSecretRoom = roomName === "Винил-рум" || roomName === "Радиола-рум";
  const [isDurationOpen, setIsDurationOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!date && !time) {
      const now = new Date(Date.now() + 15 * 60 * 1000);
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const yyyy = now.getFullYear();
      setDate(`${dd}.${mm}.${yyyy}`);

      const initialTime = `${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes()
      ).padStart(2, "0")}`;

      if (!isAllowedBookingTime(`${dd}.${mm}.${yyyy}`, initialTime)) {
        setDefaultTime("12:00");
      } else {
        setDefaultTime(initialTime);
      }
    }
    return () => {
      dispatch(resetSuccess());
      dispatch(resetError());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [errors, setErrors] = useState({
    date: "",
    guests: "",
    time: "",
    duration: "",
  });

  const phoneInitialized = useRef(false);
  const bookingNameInitialized = useRef(false);
  const durationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (durationRef.current && !durationRef.current.contains(e.target)) {
        setIsDurationOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!bookingNameInitialized.current && user?.firstName) {
      const cleaned = stripInvalidChars(user.firstName);
      if (cleaned.length > 1) {
        setBookingName(cleaned);
      }
      bookingNameInitialized.current = true;
    }
  }, [user?.firstName]);

  useEffect(() => {
    if (!phoneInitialized.current && user?.phone) {
      const dig = user.phone.replace(/\D/g, "").replace(/^7/, "").slice(0, 10);
      setPhone(dig);
      phoneInitialized.current = true;
    }
  }, [user?.phone]);

  useEffect(() => {
    setErrors({
      date: date ? checkDate(date, t) : "",
      guests: guests ? checkGuests(guests, t) : "",
      time: time ? checkTime(time, t) : "",
      duration: duration ? checkDuration(duration, isSecretRoom, t) : "",
    });
  }, [date, guests, time, duration, isSecretRoom, t]);

  useEffect(() => {
    if (
      submitAttempted &&
      (bookingName.trim() ||
        phone.length === 10 ||
        date ||
        guests ||
        time ||
        duration)
    ) {
      const timer = setTimeout(() => {
        setSubmitAttempted(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [bookingName, phone, date, guests, time, duration, submitAttempted]);

  const handleReservation = () => {
    setSubmitAttempted(true);
    if (!user?.telegramId) return;

    const bookingNameValid =
      bookingName.trim().length > 1 && !hasInvalidNameInput(bookingName);
    const phoneValid = phone.length === 10;
    const dateValid = date && !checkDate(date, t);
    const guestsValid = guests && !checkGuests(guests, t);
    const timeValid =
      time && !checkTime(time, t) && isAllowedBookingTime(date, time);
    const durationValid = duration && !checkDuration(duration, isSecretRoom, t);

    if (
      !bookingNameValid ||
      !phoneValid ||
      !dateValid ||
      !guestsValid ||
      !timeValid ||
      !durationValid
    ) {
      return;
    }

    const formattedPhone = formatPhoneDisplay(phone);
    const data = {
      bookingName: bookingName.trim().replace(/[<>&"']/g, ""),
      phone: formattedPhone,
      date,
      guests,
      time,
      duration,
      roomName,
      description: buildDescriptionForServer().replace(/[<>&"']/g, ""),
    };
    dispatch(
      createReservation({ telegramId: user.telegramId, reservationData: data })
    );
  };

  const buildDescriptionForServer = React.useCallback(() => {
    const base = (description || "").trim();
    const roomLabel = (roomName || "").trim();

    if (roomLabel && base) return `${roomLabel}: ${base}`;
    if (roomLabel && !base) return roomLabel;
    return base;
  }, [description, roomName]);

  useEffect(() => {
    if (success && user?.telegramId) {
      const formattedPhone = formatPhoneDisplay(phone);
      const newFirstName = bookingName.trim();
      const nameChanged = newFirstName.length > 1 && newFirstName !== user.firstName;
      dispatch(
        updateUser({
          ...user,
          phone: formattedPhone,
          ...(nameChanged ? { firstName: newFirstName } : {}),
          lastOrder: {
            phone: formattedPhone,
            date,
            guests,
            time,
            duration,
            roomName,
            description: buildDescriptionForServer(),
          },
        })
      );
    }
  }, [success, dispatch, user?.telegramId, buildDescriptionForServer]); // eslint-disable-line

  if (!user) return null;

  const getDurationLabel = (value) => {
    if (value === "1") return t.duration_1h;
    if (value === "1.5") return t.duration_1_5h;
    if (value === "2") return t.duration_2h;
    if (value === "2.5") return t.duration_2_5h;
    if (value === "3") return t.duration_3h;
    if (value === "3.5") return t.duration_3_5h;
    if (value === "4") return t.duration_4plus;
    return value;
  };

  return (
    <div style={{ marginBottom: "82px" }} className="reservContainer">
      <div className="reservDescription">
        <svg
          className="reservDescriptionIcon"
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.977974"
            d="M3.15599 0C1.41309 0 0 1.4925 0 3.33333V11.6667C0 13.5075 1.41309 15 3.15599 15H11.0459C12.7888 15 14.2019 13.5075 14.2019 11.6667V8.33333C14.2019 7.87333 13.8485 7.5 13.4129 7.5C12.9774 7.5 12.6239 7.87333 12.6239 8.33333V11.6667C12.6239 12.5875 11.9178 13.3333 11.0459 13.3333H3.15599C2.28414 13.3333 1.57799 12.5875 1.57799 11.6667V3.33333C1.57799 2.4125 2.28414 1.66667 3.15599 1.66667H9.46796C9.90348 1.66667 10.257 1.29333 10.257 0.833333C10.257 0.373333 9.90348 0 9.46796 0H3.15599ZM14.2019 0.833333C14 0.833333 13.7885 0.908338 13.6347 1.0675L6.75538 8.20333C6.55261 8.41416 6.34826 8.39917 6.18888 8.15084L5.39989 6.92668C5.15845 6.55084 4.65193 6.44167 4.29057 6.6925C3.92842 6.94334 3.82664 7.4625 4.06807 7.83834L4.85707 9.0625C5.56795 10.17 6.95894 10.3167 7.8655 9.375L14.7692 2.21334C15.0769 1.89417 15.0769 1.3875 14.7692 1.0675C14.6154 0.907505 14.4039 0.833333 14.2019 0.833333Z"
            fill="black"
          />
        </svg>
        <p className="reservDescriptionIconP">{t.title}</p>
      </div>

      <div className="reservContent">
        <div className="inputContent" style={{ marginTop: "15px" }}>
          <label
            className={
              nameFormatError ||
              (submitAttempted && bookingName.trim().length <= 1) ||
              (nameTouched && bookingName.trim().length === 1)
                ? "error"
                : ""
            }
          >
            {nameFormatError
              ? t.bookingNameFormatError
              : (submitAttempted && bookingName.trim().length <= 1) ||
                (nameTouched && bookingName.trim().length === 1)
              ? t.bookingNameInvalidError
              : t.bookingNameLabel}
          </label>
          <input
            value={bookingName}
            onChange={(e) => {
              const val = e.target.value;
              setBookingName(val);
              setNameFormatError(val.length > 0 && hasInvalidNameInput(val));
              setNameTouched(false);
            }}
            onBlur={() => setNameTouched(true)}
            placeholder={t.bookingNamePlaceholder}
          />
        </div>

        <div className="inputContent">
          <label
            className={submitAttempted && phone.length !== 10 ? "error" : ""}
          >
            {submitAttempted && phone.length !== 10
              ? t.phoneRequired
              : t.phoneLabelReservation}
          </label>
          <input
            value={formatPhoneDisplay(phone)}
            onChange={(e) => {
              const dig = e.target.value
                .replace(/\D/g, "")
                .replace(/^7/, "")
                .slice(0, 10);
              setPhone(dig);
            }}
            placeholder="+7 (___) ___-__-__"
          />
        </div>

        <div className="inputContent">
          <label
            className={errors.date || (submitAttempted && !date) ? "error" : ""}
          >
            {errors.date ||
              (submitAttempted && !date ? t.dateLabel : t.dateLabel)}
          </label>
          <Flatpickr
            value={date}
            onChange={([date]) => setDate(formatDate(date))}
            options={{
              locale: Russian,
              dateFormat: "d.m.Y",
              disableMobile: "true",
              minDate: "today",
            }}
            placeholder="06.06.2025"
          />
        </div>

        <div className="inputContent">
          <label
            className={
              errors.guests || (submitAttempted && !guests) ? "error" : ""
            }
          >
            {errors.guests ||
              (submitAttempted && !guests
                ? t.guestsRequired
                : t.guestsLabel)}
          </label>
          <input
            value={guests}
            onChange={(e) => setGuests(e.target.value)}
            placeholder={t.guestsPlaceholder}
          />
        </div>

        <div className="inputContent">
          <label
            className={
              (timeTouched || submitAttempted) &&
              (errors.time ||
                (checkTime(time, t) === "" &&
                  time &&
                  !isAllowedBookingTime(date, time)) ||
                (submitAttempted && !time))
                ? "error"
                : ""
            }
          >
            {timeTouched || submitAttempted
              ? errors.time
                ? errors.time
                : checkTime(time, t) === "" &&
                  time &&
                  !isAllowedBookingTime(date, time)
                ? t.timeNotWorkingError
                : submitAttempted && !time
                ? t.timeRequired
                : t.timeLabel
              : t.timeLabel}
          </label>
          <input
            value={time}
            onChange={(e) => {
              setTime(e.target.value);
              setTimeTouched(false);
            }}
            onBlur={() => setTimeTouched(true)}
            placeholder={`${t.timePlaceholderPrefix} ${defaultTime}`}
          />
        </div>

        <div className="inputContent" ref={durationRef}>
          <label
            className={
              errors.duration || (submitAttempted && !duration) ? "error" : ""
            }
          >
            {errors.duration ||
              (submitAttempted && !duration
                ? t.durationRequired
                : t.durationLabel)}
          </label>
          <button
            type="button"
            className={`durationTrigger ${duration ? "filled" : ""}`}
            onClick={() => setIsDurationOpen((prev) => !prev)}
          >
            <span>
              {duration === ""
                ? t.durationRequired
                : isSecretRoom
                ? formatSecretDurationLabel(duration, t)
                : getDurationLabel(duration)}
            </span>
            <span className={`durationArrow ${isDurationOpen ? "open" : ""}`} />
          </button>
          {isDurationOpen && (
            <div className="durationDropdown">
              {isSecretRoom ? (
                <>
                  {Array.from({ length: 19 }, (_, i) => 1 + i * 0.5).map(
                    (value) => {
                      const valueStr = String(value);
                      return (
                        <div
                          key={valueStr}
                          className="durationOption"
                          onClick={() => {
                            setDuration(valueStr);
                            setIsDurationOpen(false);
                          }}
                        >
                          {formatSecretDurationLabel(valueStr, t)}
                        </div>
                      );
                    }
                  )}
                </>
              ) : (
                <>
                  <div
                    className="durationOption"
                    onClick={() => {
                      setDuration("1");
                      setIsDurationOpen(false);
                    }}
                  >
                    {t.duration_1h}
                  </div>
                  <div
                    className="durationOption"
                    onClick={() => {
                      setDuration("1.5");
                      setIsDurationOpen(false);
                    }}
                  >
                    {t.duration_1_5h}
                  </div>
                  <div
                    className="durationOption"
                    onClick={() => {
                      setDuration("2");
                      setIsDurationOpen(false);
                    }}
                  >
                    {t.duration_2h}
                  </div>
                  <div
                    className="durationOption"
                    onClick={() => {
                      setDuration("2.5");
                      setIsDurationOpen(false);
                    }}
                  >
                    {t.duration_2_5h}
                  </div>
                  <div
                    className="durationOption"
                    onClick={() => {
                      setDuration("3");
                      setIsDurationOpen(false);
                    }}
                  >
                    {t.duration_3h}
                  </div>
                  <div
                    className="durationOption"
                    onClick={() => {
                      setDuration("3.5");
                      setIsDurationOpen(false);
                    }}
                  >
                    {t.duration_3_5h}
                  </div>
                  <div
                    className="durationOption"
                    onClick={() => {
                      setDuration("4");
                      setIsDurationOpen(false);
                    }}
                  >
                    {t.duration_4plus}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {roomName !== "" && (
          <div className="inputContent">
            <label>{t.roomLabel}</label>
            <input
              value={roomName}
              readOnly={true}
              style={{ pointerEvents: "none" }}
              placeholder={t.roomPlaceholder}
            />
          </div>
        )}

        <div className="inputContent">
          <label>{t.commentLabel}</label>
          <textarea
            className="reservDescriptionTextarea"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            placeholder={t.commentPlaceholder}
            rows={1}
          />
          {t.commentHint && (
            <span style={{ fontSize: "11px", color: "rgba(0, 0, 0, 0.4)", marginTop: "6px", display: "block", lineHeight: "1.2" }}>
              {t.commentHint}
            </span>
          )}
        </div>

        {error && !success && (
          <p style={{ color: "#c0392b", textAlign: "center", fontSize: "13px", marginBottom: "10px", lineHeight: "1.4" }}>
            {typeof error === "string" ? error : t.submitError}
          </p>
        )}

        <button
          className="reservButton"
          style={{ background: "#366091" }}
          onClick={handleReservation}
          disabled={loading || success}
        >
          {loading ? (
            t.submitting
          ) : success ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 6 }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <p>{t.success}</p>
            </div>
          ) : (
            t.submit
          )}
        </button>
      </div>
    </div>
  );
};

export default Reservation;